/**
 * 子代理工具策略、自定义工具与角色卡相关的 UI 文案分区（命名空间 `prompt-tool`）。
 *
 * 约定（与 locales.ts 相同）：zh 是键类型事实源，en 用 Record<keyof typeof zh, string>
 * 强制键集一致；只放用户可见文案，provider/model id、路径、用户内容、协议 code 不进字典。
 * 键前缀分区：`policy.`（工具与深度模块卡 + 实例级工具策略编辑器）、
 * `customTools.`（自定义工具列表容器）、`toolEditor.`（单张工具卡与字段编辑器）。
 * 角色卡页面（CharactersPage）文案在核心字典的 `characters.` 前缀；character-card.ts
 * 的解析错误不直接面向用户（PNG 走 host 上传），不进字典。
 */
export const CARDS_ZH = {
  // 子代理「工具与深度」模块卡（DelegationToolsCard；深度标签复用 param.maxDepth）。
  'policy.delegation.name': '工具与深度',
  'policy.delegation.meta': '委派递归深度（工具授权已归「subagent-tool-policy」能力卡）',
  'policy.delegation.hint': '工具授权由「subagent-tool-policy」能力卡统一维护（授权上限、工具档与模型扩权）；此处只保留子代理递归深度。',
  'policy.delegation.policyMoved': '子代理工具策略已改为模块类型能力：在模块列表里启用/删除「subagent-tool-policy」能力卡，卡内编辑授权上限、工具档与模型扩权。',
  'policy.delegation.depthHint': '0 表示禁止委派；服务商管理表示由服务商决定；正整数限制递归层数；留空使用默认值',
  'policy.delegation.depthUnset': '（不设置）',
  'policy.delegation.section': '子代理工具策略（subagentToolPolicy · 实例级授权）',

  // 实例级工具策略编辑器（SubagentToolPolicyCard）：单一开关 + 自动保存。
  'policy.title': '子代理工具策略',
  'policy.titleHint': '模型只能在用户授权上限内选择工具；策略只影响后续新实例',
  'policy.toggleLabel': '启用子代理工具策略',
  'policy.status.enabled': '已启用（{count} 档）',
  'policy.status.disabled': '已关闭',
  'policy.loading': '加载中…',
  'policy.loadFailed': '子代理工具策略读取失败，请重试后再编辑。',
  'policy.retry': '重试',
  'policy.disabledHint': '策略已关闭：下方内容只读；打开开关即写入可用骨架并自动保存（模块声明保留，子代理回落到官方委派行为）。',
  'policy.invalidBindings': '存在未选择或已失效的角色卡绑定，请删除或改绑。',
  'policy.notice.autosaved': '子代理工具策略已自动保存并重建（既有子代理不变，仅影响新实例）',
  'policy.notice.saveFailed': '策略保存失败',
  'policy.notice.previewFailed': '预览失败',
  'policy.removeBlocked': '不能删除工具档 {id}：仍被 {refs} 引用',
  'policy.ref.character': '角色 {id}',
  'policy.ref.task': '任务 {id}',
  'policy.ref.separator': '、',
  'policy.defaultProfile.label': '默认工具档',
  'policy.defaultProfile.hint': '未命中任何 selector 时使用的 profile；须引用已存在 profile。',
  'policy.ceiling.allow.label': '能力上限 allow',
  'policy.ceiling.allow.hint': 'ceiling.allow：任何 profile/角色/任务/扩权都不能越界。',
  'policy.ceiling.deny.label': '能力上限 deny',
  'policy.ceiling.deny.hint': 'ceiling.deny：永远优先，不能通过任何 selector 或 additional_tools 恢复。',
  'policy.profiles.hint': '工具档（profile）：每个档位是模型可选的授权集合；模型可选=false 不进模型参数 enum。',
  'policy.profile.id.hint': '工具档 id（模型参数 enum 值）。',
  'policy.profile.name': '名称',
  'policy.profile.name.hint': '工具档显示名称。',
  'policy.profile.namePlaceholder': '名称',
  'policy.profile.modelSelectable': '模型可选',
  'policy.profile.modelSelectable.hint': '模型可选择：false 不进模型参数 enum。',
  'policy.profile.copySuffix': '{name} 副本',
  'policy.profile.removeAria': '删除 profile {id}',
  'policy.addProfile': '添加工具档',
  'policy.moveUp': '上移',
  'policy.moveDown': '下移',
  'policy.duplicate': '复制',
  'policy.remove': '删除',
  'policy.bindings.hint': '角色卡绑定：相同角色卡在不同模块可拥有不同权限；只决定工具档，不改变 persona/世界书。',
  'policy.binding.character': '角色卡',
  'policy.binding.character.hint': '绑定角色卡 id；失效绑定须删除或改绑后才能保存。',
  'policy.binding.characterAria': '角色卡 id',
  'policy.binding.empty': '（选择角色卡）',
  'policy.binding.option': '{name}（{id}）',
  'policy.binding.missing': '失效：{id}',
  'policy.binding.profile': '工具档',
  'policy.binding.profile.hint': '该角色卡使用的 profile。',
  'policy.binding.profileAria': '绑定 profile',
  'policy.binding.modelSelectable.hint': '模型可选择该角色绑定。',
  'policy.binding.modelSelectableAria': '角色绑定模型可选择',
  'policy.binding.removeAria': '删除绑定',
  'policy.addBinding': '添加角色绑定',
  'policy.rules.hint': '任务规则：order 升序、首个匹配获胜；输入 = description + prompt；无匹配回落 default。',
  'policy.rule.id.hint': '任务规则 id。',
  'policy.rule.idAria': '任务规则 id',
  'policy.rule.name': '名称',
  'policy.rule.name.hint': '任务规则显示名。',
  'policy.rule.nameAria': '任务规则名',
  'policy.rule.pattern': '正则',
  'policy.rule.pattern.hint': '匹配 description + prompt 的正则表达式。',
  'policy.rule.patternAria': '正则',
  'policy.rule.patternPlaceholder': '(research|调研)',
  'policy.rule.order.hint': 'order 升序、首个匹配获胜。',
  'policy.rule.profile': '工具档',
  'policy.rule.profile.hint': '命中后使用的 profile。',
  'policy.rule.profileAria': '规则 profile',
  'policy.rule.modelSelectable.hint': '模型可选择该任务类型。',
  'policy.rule.modelSelectableAria': '任务规则模型可选择',
  'policy.rule.invalidPattern': '正则无效',
  'policy.rule.removeAria': '删除规则',
  'policy.addRule': '添加任务规则',
  'policy.expansion.enabled': '模型扩权',
  'policy.expansion.enabled.hint': '启用模型扩权（additional_tools）：additional_tools 严格限制在 expansion.allow ∩ ceiling.allow ∩ 模块可见工具内。',
  'policy.expansion.enabledAria': '启用模型扩权',
  'policy.expansion.approval': '扩权需人工批准',
  'policy.expansion.approval.hint': '扩权前人工批准（无批准通道 fail closed）。',
  'policy.expansion.max': '最大扩权数',
  'policy.expansion.max.hint': 'maxAdditionalTools；0 = 关闭扩权参数。',
  'policy.expansion.maxAria': '最大扩权数',
  'policy.expansion.allow.label': '扩权 allow',
  'policy.expansion.allow.hint': '模型可请求的附加工具（须在 ceiling 内）。',
  'policy.preview.title': '实例解析预览',
  'policy.preview.hint': '输入 description/prompt 与 selector，调用 host 同一 resolve seam 预览有效工具集；只读，不创建子代理。',
  'policy.preview.toolAria': '预览工具',
  'policy.preview.descriptionAria': '预览 description',
  'policy.preview.promptAria': '预览 prompt',
  'policy.preview.profileAria': '预览 tool_profile',
  'policy.preview.profileEmpty': '（不指定 tool_profile）',
  'policy.preview.characterAria': '预览 character_id',
  'policy.preview.characterEmpty': '（不指定 character_id）',
  'policy.preview.taskAria': '预览 task_type',
  'policy.preview.taskEmpty': '（不指定 task_type）',
  'policy.preview.run': '预览',
  'policy.menu.empty': '（未选择）',
  'policy.tagPlaceholder': '输入后回车添加',

  // 自定义工具容器（CustomToolsCard）。
  'customTools.aria': '自定义工具编辑',
  'customTools.fieldsAria': '自定义工具配置',
  'customTools.hint': '在此新建空白工具或从模板插入；保存后生效。模型可见工具请到「工具预览」查看。',
  'customTools.readonly': '当前模块工具只读；system 模块或未启用模块写入时不能编辑或保存。',
  'customTools.loading': '正在读取自定义工具…',
  'customTools.loadFailed': '自定义工具读取失败',
  'customTools.retry': '重试读取',
  'customTools.save': '保存',
  'customTools.saving': '保存中…',
  'customTools.saveFailed': '保存失败',
  'customTools.saved': '已保存 {count} 个自定义工具（已重建）',
  'customTools.duplicateId': '工具 id 已存在：{id}',
  'customTools.templateInserted': '已插入工具模板 {id}（保存后生效）',

  // 单张工具卡与字段编辑器（CustomToolEditor）。
  'toolEditor.params.label': 'parameters（模型可见参数 schema）',
  'toolEditor.params.empty': '无参数；下方添加。required=true 时模型必须提供该参数。',
  'toolEditor.params.nameAria': '参数名',
  'toolEditor.params.namePlaceholder': '参数名',
  'toolEditor.params.typeAria': '参数类型',
  'toolEditor.params.requiredHint': '模型必须填写此参数',
  'toolEditor.params.requiredAria': '必填',
  'toolEditor.params.descriptionAria': '参数描述',
  'toolEditor.params.descriptionPlaceholder': '描述',
  'toolEditor.params.removeAria': '删除参数 {key}',
  'toolEditor.params.remove': '删除',
  'toolEditor.params.add': '添加参数',
  'toolEditor.json.hint': '失焦提交合法 JSON；嵌套属性、items、oneOf、enum 等在此编辑。',
  'toolEditor.json.mustBeObject': '必须是 JSON 对象',
  'toolEditor.json.invalid': 'JSON 无效；修正后再保存工具',
  'toolEditor.json.advanced': '高级参数 JSON',
  'toolEditor.json.output': '输出 schema JSON',
  'toolEditor.chip.params': '{count} 参数',
  'toolEditor.enable.hint.on': '点击停用；停用后不注册工具',
  'toolEditor.enable.hint.off': '点击启用',
  'toolEditor.enable.aria': '启用工具 {id}',
  'toolEditor.moveUp': '上移',
  'toolEditor.moveDown': '下移',
  'toolEditor.duplicate': '复制',
  'toolEditor.remove': '删除',
  'toolEditor.confirmRemove': '确认删除',
  'toolEditor.cancel': '取消',
  'toolEditor.field.id': 'id（文件标识）',
  'toolEditor.field.idAria': '工具 id',
  'toolEditor.field.name': 'name（模型可见名）',
  'toolEditor.field.nameAria': '工具名',
  'toolEditor.field.description': 'description（模型可见描述）',
  'toolEditor.field.descriptionAria': '工具描述',
  'toolEditor.field.descriptionPlaceholder': '描述该工具给模型看',
  'toolEditor.field.kind': 'execute.kind（执行器）',
  'toolEditor.field.kind.hint': 'shell=命令；http=请求；delegate=委托内置/已注册工具；fs=工作区文件；ask-user=询问用户',
  'toolEditor.field.kindAria': '执行器',
  'toolEditor.field.command.hint': '{{args.x}} 参数插值；env 白名单；cwd=会话工作区',
  'toolEditor.field.commandAria': 'shell 命令',
  'toolEditor.field.shell.hint': 'pwsh 强制 UTF-8 输出（中文不乱码）',
  'toolEditor.field.url.hint': '{{args.x}} 参数插值',
  'toolEditor.field.urlAria': '请求 URL',
  'toolEditor.field.methodAria': '请求方法',
  'toolEditor.field.delegate.hint': '内置工具：{names}',
  'toolEditor.field.delegate': 'tool（委托目标）',
  'toolEditor.field.delegateAria': '委托目标工具',
  'toolEditor.field.fsActionAria': 'fs 动作',
  'toolEditor.field.path.hint': '相对工作区路径；越界拒绝',
  'toolEditor.field.pathAria': '文件路径',
  'toolEditor.field.content.hint': '写入或追加的文本；支持 {{args.x}}，空文本会写入空内容',
  'toolEditor.field.contentAria': '文件内容',
  'toolEditor.field.question': 'question（向用户确认的问题）',
  'toolEditor.field.questionAria': '询问问题',
  'toolEditor.field.questionPlaceholder': '是否继续执行该操作？',
  'toolEditor.timeout.hint': '正整数毫秒，留空使用执行器默认；最大 2147483647。',
  'toolEditor.timeoutAria': '工具超时毫秒',
  'toolEditor.timeout.invalid': '超时必须是 1–2147483647 的整数',
  'importPreview.title': '导入预览（先确认再写入）',
  'importPreview.summary': '来源条目 {inputs} · 生成配置 {converted} · 禁用 {disabled} · 降级 {degraded} · 不支持 {unsupported} · 被排除 {excluded}',
  'importPreview.review': '需要确认：{count} 项',
  'importPreview.reviewAria': '需要确认的有损信息，共 {count} 项',
  'importPreview.info': '提示（不改变写入结果）：{count} 项',
  'importPreview.infoAria': '提示级降级信息，共 {count} 项',
  'importPreview.excluded': '被排除条目：{count} 项',
  'importPreview.excludedAria': '被排除条目，共 {count} 项',
  'importPreview.excludedItem': '来源条目 {source}（{codes}）',
  'importPreview.diagSource': '来源 {source}',
  'importPreview.diagTarget': '目标 {target}',
  'importPreview.none': '未发现降级、不支持或排除项',
  'importPreview.truncated': '报告已达服务端上限：已展示 {entries} 条条目、{diagnostics} 条诊断，来源条目共 {inputs} 条；需要完整复核时请拆分导入。',
  'importPreview.groups': '顺序组（选择要应用的 prompt_order 分组）',
  'importPreview.groupsAria': '选择顺序组',
  'importPreview.pickGroup': '请选择顺序组',
  'importPreview.needsGroup': '{source} 含多个 prompt_order 分组，无法确定使用哪个角色：先选择顺序组，确认后会重新预览。',
  'importPreview.groupOption': '角色 {id}（{entries} 条）',
  'importPreview.confirm': '确认导入',
  'importPreview.cancel': '取消预览',
  'importPreview.noReport': '该来源没有可转换的 SillyTavern 内容，确认后按原样导入',
  'importPreview.stale': '预览已过期（文件、顺序组或目标已变化），请重新预览后再导入',
  'assetImport.presetTitle': '导入模块',
  'assetImport.characterTitle': '导入角色卡',
  'assetImport.select': '选择来源',
  'assetImport.inspect': '检查内容',
  'assetImport.result': '导入结果',
  'assetImport.steps': '导入步骤',
  'assetImport.close': '关闭导入',
  'assetImport.formats': '支持 ZIP、JSON、PNG、YML、YAML，也可选择模块文件夹。',
  'assetImport.noWrite': '选择后自动识别和预览；确认前不会写入模块或角色库。',
  'assetImport.file': '选择文件',
  'assetImport.folder': '选择文件夹',
  'assetImport.queue': '第 {index} / {total} 张',
  'assetImport.source': '来源：{name}',
  'assetImport.avatar': '{name} 的原图预览',
  'assetImport.avatarUnavailable': '无法显示图片预览，仍将检查原始文件。',
  'assetImport.reading': '正在读取和检查；旧预览暂不可确认。',
  'assetImport.submitting': '正在安装，请稍候。',
  'assetImport.target': '保存到',
  'assetImport.name': '名称',
  'assetImport.id': '标识',
  'assetImport.invalidId': '标识以小写字母或数字开头，仅可包含小写字母、数字和短横线，最多 128 字符。',
  'assetImport.new': '另存为新项目',
  'assetImport.existing': '更新现有项目',
  'assetImport.newHint': '将新增一个项目；导入完成后可显式切换或应用。',
  'assetImport.presetReplace': '替换：模块定义和此次提供的自有资源。保留：角色记忆和不归本次导入所有的文件。',
  'assetImport.characterReplace': '替换：角色来源、转换配置和此次提供的头像。保留：角色记忆和其他文件。',
  'assetImport.summary': '{files} 个文件 · {bytes} 字节 · {configs} 条提示词配置',
  'assetImport.files': '文件与资源（{count}）',
  'assetImport.add': '新增',
  'assetImport.replace': '替换／合并',
  'assetImport.warnings': '需要检查',
  'assetImport.kind': '选择来源类型',
  'assetImport.kind.native-preset': '原生模块',
  'assetImport.kind.native-character': '原生角色片段',
  'assetImport.kind.st-preset': 'SillyTavern 预设',
  'assetImport.kind.st-character': 'SillyTavern 角色卡',
  'assetImport.kind.world-book': '世界书',
  'assetImport.chooseFirst': '请完成来源、顺序组或目标选择后再确认。',
  'assetImport.createPreset': '创建模块',
  'assetImport.createFromCharacter': '从角色卡创建模块',
  'assetImport.createCharacter': '保存为新角色',
  'assetImport.update': '更新项目',
  'assetImport.overwriteTitle': '确认更新“{name}”',
  'assetImport.back': '返回检查',
  'assetImport.done': '完成',
  'assetImport.cancel': '取消',
  'assetImport.end': '结束本次导入',
  'assetImport.skip': '跳过这张',
  'assetImport.reselect': '重新选择',
  'assetImport.repreview': '重新预览',
  'assetImport.stale': '预览已过期，尚未提交。已保留所选文件，请重新预览。',
  'assetImport.counts': '已导入 {imported} 项 · 跳过 {skipped} 项 · 失败 {failed} 项',
  'assetImport.refreshFailed': '已导入，但列表刷新失败：{reason}。请重试刷新，无需重新导入。',
  'assetImport.refresh': '重试刷新',
  'assetImport.switch': '切换到该模块',
  'assetImport.apply': '应用到当前模块',
  'assetImport.directoryTooLarge': '文件夹超过传输上限，请压缩为 ZIP 后导入。',
  'assetImport.draftBlocked': '当前模块仍有未保存草稿，请先保存或修正草稿，再重新预览覆盖操作。',
  'assetExport.title': '导出模块 · {name}',
  'assetExport.scope': '导出范围',
  'assetExport.zip': '完整模块 ZIP',
  'assetExport.zipHint': '包含定义、正文、图片与自有模块；运行需兼容的 Prompt Tool 与宿主版本。',
  'assetExport.definition': '仅模块定义 YAML',
  'assetExport.definitionHint': '用于查看或分享配置，不包含引用的附件。',
  'assetExport.excluded': '已排除 {count} 条经核验的自动记忆配置。',
  'assetExport.boundary': '不包含角色记忆文件、工作区指令、技能库和机器配置。',
  'assetExport.conflicts': '以下配置的记忆来源不明，请逐项选择后重新检查。',
  'assetExport.include': '保留内容',
  'assetExport.exclude': '排除内容',
  'assetExport.included': '此次导出包含你选择保留的疑似记忆内容。',
  'assetExport.preview': '重新检查',
  'assetExport.blocked': '无法生成完整包，请修复以下资源或改为仅模块定义。',
  'assetExport.downloadZip': '导出 ZIP',
  'assetExport.downloadYaml': '导出 YAML',
  'assetExport.downloaded': '已生成，浏览器已开始下载。',
}

export const CARDS_EN: Record<keyof typeof CARDS_ZH, string> = {
  'policy.delegation.name': 'Tools & depth',
  'policy.delegation.meta': 'Delegation recursion depth (tool grants live in the "subagent-tool-policy" card)',
  'policy.delegation.hint': 'Tool grants are maintained by the "subagent-tool-policy" capability card (ceiling, profiles and model expansion); only subagent recursion depth lives here.',
  'policy.delegation.policyMoved': 'The subagent tool policy is now a module-type capability: enable or remove the "subagent-tool-policy" card in the module list and edit the ceiling, profiles and model expansion inside it.',
  'policy.delegation.depthHint': '0 forbids delegation; provider-managed leaves it to the provider; a positive integer limits recursion depth; empty uses the default',
  'policy.delegation.depthUnset': '(Not set)',
  'policy.delegation.section': 'Subagent tool policy (subagentToolPolicy · per-instance grants)',

  'policy.title': 'Subagent tool policy',
  'policy.titleHint': 'The model can only pick tools within the user-authorized ceiling; the policy affects new instances only',
  'policy.status.enabled': 'Enabled ({count} profiles)',
  'policy.status.disabled': 'Disabled',
  'policy.toggleLabel': 'Enable subagent tool policy',
  'policy.loading': 'Loading…',
  'policy.loadFailed': 'Failed to load the subagent tool policy. Retry before editing.',
  'policy.retry': 'Retry',
  'policy.disabledHint': 'Policy is off: the content below is read-only. Turning the switch on writes a usable skeleton and saves it automatically (the module declaration stays; subagents fall back to official delegation behavior).',
  'policy.invalidBindings': 'Some character bindings are empty or stale; remove or re-point them.',
  'policy.notice.autosaved': 'Subagent tool policy auto-saved and rebuilt (existing subagents unchanged; only new instances are affected)',
  'policy.notice.saveFailed': 'Failed to save the policy',
  'policy.notice.previewFailed': 'Preview failed',
  'policy.removeBlocked': 'Cannot delete tool profile {id}: still referenced by {refs}',
  'policy.ref.character': 'character {id}',
  'policy.ref.task': 'task {id}',
  'policy.ref.separator': ', ',
  'policy.defaultProfile.label': 'Default profile',
  'policy.defaultProfile.hint': 'Profile used when no selector matches; must reference an existing profile.',
  'policy.ceiling.allow.label': 'Ceiling allow',
  'policy.ceiling.allow.hint': 'ceiling.allow: no profile, character, task or expansion may cross this boundary.',
  'policy.ceiling.deny.label': 'Ceiling deny',
  'policy.ceiling.deny.hint': 'ceiling.deny: always wins; no selector or additional_tools can restore it.',
  'policy.profiles.hint': 'Tool profiles: each profile is a grant set the model may pick; modelSelectable=false stays out of the model parameter enum.',
  'policy.profile.id.hint': 'Tool profile id (the model parameter enum value).',
  'policy.profile.name': 'Name',
  'policy.profile.name.hint': 'Display name of the tool profile.',
  'policy.profile.namePlaceholder': 'Name',
  'policy.profile.modelSelectable': 'Model selectable',
  'policy.profile.modelSelectable.hint': 'Model selectable: false stays out of the model parameter enum.',
  'policy.profile.copySuffix': '{name} copy',
  'policy.profile.removeAria': 'Delete profile {id}',
  'policy.addProfile': 'Add tool profile',
  'policy.moveUp': 'Move up',
  'policy.moveDown': 'Move down',
  'policy.duplicate': 'Duplicate',
  'policy.remove': 'Delete',
  'policy.bindings.hint': 'Character bindings: the same character card may hold different grants per module; they only pick a tool profile and never change persona or world book.',
  'policy.binding.character': 'Character',
  'policy.binding.character.hint': 'Bind a character card id; stale bindings must be removed or re-pointed before saving.',
  'policy.binding.characterAria': 'Character card id',
  'policy.binding.empty': '(Select a character)',
  'policy.binding.option': '{name} ({id})',
  'policy.binding.missing': 'Missing: {id}',
  'policy.binding.profile': 'Tool profile',
  'policy.binding.profile.hint': 'Profile used by this character card.',
  'policy.binding.profileAria': 'Bound profile',
  'policy.binding.modelSelectable.hint': 'The model may select this character binding.',
  'policy.binding.modelSelectableAria': 'Character binding model selectable',
  'policy.binding.removeAria': 'Delete binding',
  'policy.addBinding': 'Add character binding',
  'policy.rules.hint': 'Task rules: ascending order, first match wins; input = description + prompt; no match falls back to default.',
  'policy.rule.id.hint': 'Task rule id.',
  'policy.rule.idAria': 'Task rule id',
  'policy.rule.name': 'Name',
  'policy.rule.name.hint': 'Display name of the task rule.',
  'policy.rule.nameAria': 'Task rule name',
  'policy.rule.pattern': 'Pattern',
  'policy.rule.pattern.hint': 'Regular expression matched against description + prompt.',
  'policy.rule.patternAria': 'Pattern',
  'policy.rule.patternPlaceholder': '(research|analysis)',
  'policy.rule.order.hint': 'Ascending order; first match wins.',
  'policy.rule.profile': 'Tool profile',
  'policy.rule.profile.hint': 'Profile used after a match.',
  'policy.rule.profileAria': 'Rule profile',
  'policy.rule.modelSelectable.hint': 'The model may select this task type.',
  'policy.rule.modelSelectableAria': 'Task rule model selectable',
  'policy.rule.invalidPattern': 'Invalid pattern',
  'policy.rule.removeAria': 'Delete rule',
  'policy.addRule': 'Add task rule',
  'policy.expansion.enabled': 'Model expansion',
  'policy.expansion.enabled.hint': 'Enable model expansion (additional_tools): additional_tools is strictly limited to expansion.allow ∩ ceiling.allow ∩ module-visible tools.',
  'policy.expansion.enabledAria': 'Enable model expansion',
  'policy.expansion.approval': 'Expansion needs approval',
  'policy.expansion.approval.hint': 'Require manual approval before expansion (fails closed without an approval channel).',
  'policy.expansion.max': 'Max expansions',
  'policy.expansion.max.hint': 'maxAdditionalTools; 0 = disable the expansion parameter.',
  'policy.expansion.maxAria': 'Max expansions',
  'policy.expansion.allow.label': 'Expansion allow',
  'policy.expansion.allow.hint': 'Additional tools the model may request (must stay within the ceiling).',
  'policy.preview.title': 'Instance resolution preview',
  'policy.preview.hint': 'Enter description/prompt and selectors to preview the effective tool set through the same host resolve seam; read-only, no subagent is created.',
  'policy.preview.toolAria': 'Preview tool',
  'policy.preview.descriptionAria': 'Preview description',
  'policy.preview.promptAria': 'Preview prompt',
  'policy.preview.profileAria': 'Preview tool_profile',
  'policy.preview.profileEmpty': '(No tool_profile)',
  'policy.preview.characterAria': 'Preview character_id',
  'policy.preview.characterEmpty': '(No character_id)',
  'policy.preview.taskAria': 'Preview task_type',
  'policy.preview.taskEmpty': '(No task_type)',
  'policy.preview.run': 'Preview',
  'policy.menu.empty': '(Not selected)',
  'policy.tagPlaceholder': 'Press Enter to add',

  'customTools.aria': 'Custom tool editor',
  'customTools.fieldsAria': 'Custom tool configuration',
  'customTools.hint': 'Create a blank tool or insert a template here, then save to apply. Inspect model-visible tools on the Tools preview page.',
  'customTools.readonly': 'Tools of the current module are read-only; system modules or modules without write access cannot be edited or saved.',
  'customTools.loading': 'Reading custom tools…',
  'customTools.loadFailed': 'Failed to read custom tools',
  'customTools.retry': 'Retry',
  'customTools.save': 'Save',
  'customTools.saving': 'Saving…',
  'customTools.saveFailed': 'Save failed',
  'customTools.saved': 'Saved {count} custom tools (rebuilt)',
  'customTools.duplicateId': 'Tool id already exists: {id}',
  'customTools.templateInserted': 'Inserted tool template {id} (applies after saving)',

  'toolEditor.params.label': 'parameters (model-visible parameter schema)',
  'toolEditor.params.empty': 'No parameters yet; add them below. required=true forces the model to provide the parameter.',
  'toolEditor.params.nameAria': 'Parameter name',
  'toolEditor.params.namePlaceholder': 'Name',
  'toolEditor.params.typeAria': 'Parameter type',
  'toolEditor.params.requiredHint': 'The model must provide this parameter',
  'toolEditor.params.requiredAria': 'Required',
  'toolEditor.params.descriptionAria': 'Parameter description',
  'toolEditor.params.descriptionPlaceholder': 'Description',
  'toolEditor.params.removeAria': 'Delete parameter {key}',
  'toolEditor.params.remove': 'Delete',
  'toolEditor.params.add': 'Add parameter',
  'toolEditor.json.hint': 'Committed on blur when the JSON is valid; nested properties, items, oneOf, enum are edited here.',
  'toolEditor.json.mustBeObject': 'Must be a JSON object',
  'toolEditor.json.invalid': 'Invalid JSON; fix it before saving the tool',
  'toolEditor.json.advanced': 'Advanced parameters JSON',
  'toolEditor.json.output': 'Output schema JSON',
  'toolEditor.chip.params': '{count} params',
  'toolEditor.enable.hint.on': 'Click to disable; a disabled tool is not registered',
  'toolEditor.enable.hint.off': 'Click to enable',
  'toolEditor.enable.aria': 'Enable tool {id}',
  'toolEditor.moveUp': 'Move up',
  'toolEditor.moveDown': 'Move down',
  'toolEditor.duplicate': 'Duplicate',
  'toolEditor.remove': 'Delete',
  'toolEditor.confirmRemove': 'Confirm delete',
  'toolEditor.cancel': 'Cancel',
  'toolEditor.field.id': 'id (file identifier)',
  'toolEditor.field.idAria': 'Tool id',
  'toolEditor.field.name': 'name (model-visible name)',
  'toolEditor.field.nameAria': 'Tool name',
  'toolEditor.field.description': 'description (model-visible description)',
  'toolEditor.field.descriptionAria': 'Tool description',
  'toolEditor.field.descriptionPlaceholder': 'Describe the tool for the model',
  'toolEditor.field.kind': 'execute.kind (executor)',
  'toolEditor.field.kind.hint': 'shell=command; http=request; delegate=delegate to a built-in or registered tool; fs=workspace files; ask-user=ask the user',
  'toolEditor.field.kindAria': 'Executor',
  'toolEditor.field.command.hint': '{{args.x}} interpolation; env allowlist; cwd=session workspace',
  'toolEditor.field.commandAria': 'Shell command',
  'toolEditor.field.shell.hint': 'pwsh forces UTF-8 output (no CJK mojibake)',
  'toolEditor.field.url.hint': '{{args.x}} interpolation',
  'toolEditor.field.urlAria': 'Request URL',
  'toolEditor.field.methodAria': 'Request method',
  'toolEditor.field.delegate.hint': 'Built-in tools: {names}',
  'toolEditor.field.delegate': 'tool (delegate target)',
  'toolEditor.field.delegateAria': 'Delegate target tool',
  'toolEditor.field.fsActionAria': 'fs action',
  'toolEditor.field.path.hint': 'Path relative to the workspace; escapes are rejected',
  'toolEditor.field.pathAria': 'File path',
  'toolEditor.field.content.hint': 'Text to write or append; supports {{args.x}}; empty text writes empty content',
  'toolEditor.field.contentAria': 'File content',
  'toolEditor.field.question': 'question (asks the user to confirm)',
  'toolEditor.field.questionAria': 'Question',
  'toolEditor.field.questionPlaceholder': 'Continue with this operation?',
  'toolEditor.timeout.hint': 'Positive integer milliseconds; empty uses the executor default; max 2147483647.',
  'toolEditor.timeoutAria': 'Tool timeout milliseconds',
  'toolEditor.timeout.invalid': 'Timeout must be an integer between 1 and 2147483647',
  'importPreview.title': 'Import preview (confirm before writing)',
  'importPreview.summary': 'Source entries {inputs} · configs {converted} · disabled {disabled} · degraded {degraded} · unsupported {unsupported} · excluded {excluded}',
  'importPreview.review': 'Needs review: {count}',
  'importPreview.reviewAria': 'Lossy items that need review, {count} in total',
  'importPreview.info': 'Notes (do not change the result): {count}',
  'importPreview.infoAria': 'Informational downgrades, {count} in total',
  'importPreview.excluded': 'Excluded entries: {count}',
  'importPreview.excludedAria': 'Excluded entries, {count} in total',
  'importPreview.excludedItem': 'Source entry {source} ({codes})',
  'importPreview.diagSource': 'Source {source}',
  'importPreview.diagTarget': 'Target {target}',
  'importPreview.none': 'No degraded, unsupported or excluded items',
  'importPreview.truncated': 'The report hit the server limit: showing {entries} entries and {diagnostics} diagnostics out of {inputs} source entries; split the import to review everything.',
  'importPreview.groups': 'Order group (pick the prompt_order group to apply)',
  'importPreview.groupsAria': 'Choose order group',
  'importPreview.pickGroup': 'Pick an order group',
  'importPreview.needsGroup': '{source} contains several prompt_order groups, so the character cannot be determined: pick an order group and the preview runs again.',
  'importPreview.groupOption': 'Character {id} ({entries} entries)',
  'importPreview.confirm': 'Confirm import',
  'importPreview.cancel': 'Cancel preview',
  'importPreview.noReport': 'No SillyTavern content to convert in this source; confirm to import as-is',
  'importPreview.stale': 'Preview is stale (file, order group or target changed); preview again',
  'assetImport.presetTitle': 'Import module',
  'assetImport.characterTitle': 'Import character cards',
  'assetImport.select': 'Select source',
  'assetImport.inspect': 'Check contents',
  'assetImport.result': 'Import result',
  'assetImport.steps': 'Import steps',
  'assetImport.close': 'Close import',
  'assetImport.formats': 'Supports ZIP, JSON, PNG, YML, YAML and module folders.',
  'assetImport.noWrite': 'Files are detected and previewed before confirmation. No module or character is written yet.',
  'assetImport.file': 'Choose files',
  'assetImport.folder': 'Choose folder',
  'assetImport.queue': 'Card {index} / {total}',
  'assetImport.source': 'Source: {name}',
  'assetImport.avatar': 'Original image preview for {name}',
  'assetImport.avatarUnavailable': 'Image preview unavailable; the original file will still be checked.',
  'assetImport.reading': 'Reading and checking; the previous preview cannot be confirmed.',
  'assetImport.submitting': 'Installing, please wait.',
  'assetImport.target': 'Save to',
  'assetImport.name': 'Name',
  'assetImport.id': 'Identifier',
  'assetImport.invalidId': 'Start with a lowercase letter or number; use lowercase letters, numbers and hyphens, up to 128 characters.',
  'assetImport.new': 'Save as a new item',
  'assetImport.existing': 'Update an existing item',
  'assetImport.newHint': 'Creates a new item. Switch or apply explicitly after importing.',
  'assetImport.presetReplace': 'Replaces the module definition and supplied owned resources. Preserves character memory and files not owned by this import.',
  'assetImport.characterReplace': 'Replaces the character source, converted configuration and supplied avatar. Preserves character memory and other files.',
  'assetImport.summary': '{files} files · {bytes} bytes · {configs} prompt configurations',
  'assetImport.files': 'Files and resources ({count})',
  'assetImport.add': 'Add',
  'assetImport.replace': 'Replace / merge',
  'assetImport.warnings': 'Needs review',
  'assetImport.kind': 'Choose source type',
  'assetImport.kind.native-preset': 'Native module',
  'assetImport.kind.native-character': 'Native character fragment',
  'assetImport.kind.st-preset': 'SillyTavern preset',
  'assetImport.kind.st-character': 'SillyTavern character card',
  'assetImport.kind.world-book': 'World book',
  'assetImport.chooseFirst': 'Finish choosing the source, order group or target before confirming.',
  'assetImport.createPreset': 'Create module',
  'assetImport.createFromCharacter': 'Create module from character',
  'assetImport.createCharacter': 'Save as new character',
  'assetImport.update': 'Update item',
  'assetImport.overwriteTitle': 'Confirm updating “{name}”',
  'assetImport.back': 'Back to review',
  'assetImport.done': 'Done',
  'assetImport.cancel': 'Cancel',
  'assetImport.end': 'End this import',
  'assetImport.skip': 'Skip this card',
  'assetImport.reselect': 'Choose again',
  'assetImport.repreview': 'Preview again',
  'assetImport.stale': 'The preview expired; nothing was submitted. Files are retained. Preview again.',
  'assetImport.counts': 'Imported {imported} · skipped {skipped} · failed {failed}',
  'assetImport.refreshFailed': 'Imported, but refreshing the list failed: {reason}. Retry refresh without importing again.',
  'assetImport.refresh': 'Retry refresh',
  'assetImport.switch': 'Switch to this module',
  'assetImport.apply': 'Apply to current module',
  'assetImport.directoryTooLarge': 'The folder exceeds the transfer limit. Compress it as ZIP and import again.',
  'assetImport.draftBlocked': 'The current module has unsaved drafts. Save or fix them, then preview the replacement again.',
  'assetExport.title': 'Export module · {name}',
  'assetExport.scope': 'Export scope',
  'assetExport.zip': 'Complete module ZIP',
  'assetExport.zipHint': 'Includes definition, text, images and owned modules. Requires compatible Prompt Tool and host versions.',
  'assetExport.definition': 'Module definition YAML only',
  'assetExport.definitionHint': 'For viewing or sharing configuration; referenced attachments are not included.',
  'assetExport.excluded': 'Excluded {count} verified automatic memory configurations.',
  'assetExport.boundary': 'Excludes character memory files, workspace instructions, skill libraries and machine configuration.',
  'assetExport.conflicts': 'The memory origin of these entries is unclear. Choose each entry, then check again.',
  'assetExport.include': 'Keep contents',
  'assetExport.exclude': 'Exclude contents',
  'assetExport.included': 'This export includes the possible memory contents you chose to keep.',
  'assetExport.preview': 'Check again',
  'assetExport.blocked': 'Cannot create a complete package. Fix these resources or choose the definition only.',
  'assetExport.downloadZip': 'Export ZIP',
  'assetExport.downloadYaml': 'Export YAML',
  'assetExport.downloaded': 'Generated; the browser has started the download.',
}
